/**
 * #280 — game-local position snapshot for the trading card.
 *
 * "What do I already hold on the market I am about to trade?", answered
 * in-place so the trader does not have to leave the ticket for the Positions
 * page. Read-only: quantity, venue-native side, and the canonical before-fees
 * average entry. No P&L, no order actions.
 *
 * Follows the panel's EFFECTIVE game (the chip-aware `game` binding), so
 * switching to a spread, total, or prop chip re-points the snapshot at that
 * market — a moneyline holding does not follow you onto the spread.
 *
 * Sits directly above `PairContextCard`, which answers a different question
 * (match-level blended pair cost and residual exposure, Kalshi-only, folded
 * from the fills ledger). This card is the per-market canonical read; the two
 * are framed differently on purpose and neither restates the other.
 *
 * The projection in `gamePositionProjection.ts` owns every accounting rule.
 * Display lives here — including the quantity contract, which is the one thing
 * the projection deliberately does not decide.
 */

import { useMemo } from 'react';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import { useAccountStore } from '@/stores/accountStore';
import { polyPositionBetLabel } from '@/components/cards/positionCardModel';
import { fmtCents, fmtQty } from '@/components/cards/cardFormat';
import { describeMarket } from '@/lib/kalshiTicker/describeMarket';
import type { KalshiPosition } from '@/types/kalshi';
import type { UserPositionSlice } from '@/hooks/useUserKalshiPOS';
import {
  projectGamePosition,
  type GamePositionProjection,
  type GamePositionRow,
  type PositionVenue,
} from './gamePositionProjection';
import type { GameRow } from './types';
import { formatUsDecimal } from '@/lib/polymarketUs/format';
import type { GamePositionUsInput } from './gamePositionProjection';
import type { UsGameVenueState } from './us/useUsGameVenue';
import type { UsGameOrders } from './useGameUsOpenOrders';

const VENUE_LABEL: Record<PositionVenue, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Polymarket',
  polymarket_us: 'Polymarket US',
};

/**
 * #1233 — the US half of the snapshot, from what Game Detail already reads:
 * the selected line's exact US market (`useUsGameVenue`) and the trader's
 * holdings from the panel's one US orders read (`useGameUsOpenOrders`), so the
 * held line and the open-order rows never come from different reads.
 *
 * A market US does not list here, or no US for this login, is `absent`; a
 * market that could not be resolved, a failed or stale read, or any account
 * whose holdings are unknown is `error`, so the card never calls a blind
 * read flat.
 */
export function usSnapshotInput(venue: UsGameVenueState, orders: UsGameOrders): GamePositionUsInput {
  const none = (status: GamePositionUsInput['status']): GamePositionUsInput =>
    ({ marketSlug: null, status, holdings: [], outcomeLabel: null });
  if (orders.phase === 'off') return none('absent');
  // A login with no US account at all: nothing to hold, whatever the catalogue says.
  if (orders.phase === 'ready' && orders.staleCode === null && orders.holdings.accounts.length === 0
    && orders.holdings.failedAccounts === 0) return none('absent');
  if (venue.status === 'off' || venue.status === 'no-market') return none('absent');
  if (venue.status === 'loading') return none('loading');
  if (venue.status !== 'ready') return none('error');
  const { market } = venue;
  const outcomeLabel = market.yesTeam
    ? (market.line ? `${market.yesTeam} ${market.line}` : market.yesTeam)
    : (market.title ?? market.marketSlug);
  if (orders.phase === 'loading') return { marketSlug: market.marketSlug, status: 'loading', holdings: [], outcomeLabel };
  if (orders.phase === 'error') return { marketSlug: market.marketSlug, status: 'error', holdings: [], outcomeLabel };
  const { holdings } = orders;
  const complete = orders.staleCode === null && holdings.failedAccounts === 0
    && holdings.accounts.every((a) => a.positionsError === null);
  return { marketSlug: market.marketSlug, status: complete ? 'ready' : 'error', holdings: holdings.accounts, outcomeLabel };
}

/**
 * Orientation comes from the ticker, via the repo's canonical helper. Defined at
 * module scope so its identity is stable across renders.
 *
 * `betTeam` is the market's named (YES) outcome regardless of the side passed —
 * the `(BAL) NO` convention — which is exactly what this card wants: a NO row
 * should name the market it settles against, not the team the trader is
 * implicitly backing. `describeMarket` reads `useMarketStore.getState()`, so a
 * label can resolve from `null` to a name once the market frame lands; the 5s
 * position poll re-runs the projection, so it converges without extra wiring.
 */
const kalshiOutcomeLabel = (ticker: string, side: 'yes' | 'no'): string | null =>
  describeMarket(ticker, side).betTeam;

/**
 * The quantity contract for this surface.
 *
 * #144's `fmtQty` is the canonical user-facing contract-count display. The
 * projection retains the exact source quantity for calculations and tests; this
 * card applies the accepted whole-number presentation only at render time.
 */
export function formatHeldQuantity(qty: number): string {
  return fmtQty(qty);
}

/** Adapt the exact Kalshi position source used by Position Tracker to the
 * ticker-keyed shape consumed by the pure game projection. The store normally
 * carries one aggregate per ticker/side. Folding defensively avoids dropping
 * exposure if a transitional payload ever contains more than one row. */
export function accountPositionsToSlices(
  positions: readonly KalshiPosition[],
  tickers: ReadonlySet<string>,
): Map<string, UserPositionSlice> {
  type SideAcc = { count: number; cost: number; basisKnown: boolean };
  const accumulators = new Map<string, { yes: SideAcc; no: SideAcc }>();

  for (const position of positions) {
    if (!tickers.has(position.ticker) || position.position <= 0) continue;
    const acc = accumulators.get(position.ticker) ?? {
      yes: { count: 0, cost: 0, basisKnown: true },
      no: { count: 0, cost: 0, basisKnown: true },
    };
    const side = acc[position.side];
    side.count += position.position;
    if (Number.isFinite(position.averagePrice) && position.averagePrice > 0) {
      side.cost += position.averagePrice * position.position;
    } else {
      side.basisKnown = false;
    }
    accumulators.set(position.ticker, acc);
  }

  const result = new Map<string, UserPositionSlice>();
  for (const [ticker, acc] of accumulators) {
    const average = (side: SideAcc): number | null =>
      side.basisKnown && side.count > 0
        ? Math.round((side.cost / side.count) * 100) / 100
        : null;
    result.set(ticker, {
      yesCount: acc.yes.count,
      noCount: acc.no.count,
      yesAvgCents: average(acc.yes),
      noAvgCents: average(acc.no),
    });
  }
  return result;
}

/** Held quantity + average, in PR #273's single-sourced wording: an absent
 *  average is spelled out, never rendered as a price.
 *
 *  `ladder/heldContractsTitle.ts` owns this convention for the Kalshi hover
 *  rings. It cannot be called here — it takes a Kalshi `UserPositionSlice` and
 *  folds BOTH sides into one string, whereas this card renders one row per side
 *  and also carries Polymarket rows, which have no slice. The wording and the
 *  0.01¢ grain are kept identical to it on purpose, and pinned by a test that
 *  compares the two outputs directly. */
function heldClause(row: GamePositionRow): string {
  // #1233 — US quantities are exact decimal text, shown as the side rail
  // shows them ("—" unknown, "*" partly known); no average until #1199.
  const exact = row.exactQuantity;
  const qty = exact
    ? `${exact.text === null ? '—' : formatUsDecimal(exact.text)}${exact.partlyKnown ? '*' : ''}`
    : formatHeldQuantity(row.quantity);
  return row.averageEntryCents == null
    ? `${qty} (avg unavailable)`
    : `${qty} @ ${fmtCents(row.averageEntryCents)} avg`;
}

/**
 * The one line the card is allowed to say about itself.
 *
 * Order matters: a broken source outranks everything, because the failure mode
 * this component exists to avoid is a blind read presented as a flat book. Only
 * once every applicable source has reported may an empty list be called "no
 * position".
 */
function snapshotNotice(projection: GamePositionProjection): string | null {
  const venues = Object.keys(projection.sources) as PositionVenue[];
  const failed = (v: PositionVenue) => {
    const state = projection.sources[v];
    return state === 'error' || state === 'unavailable';
  };
  const loading = (v: PositionVenue) => projection.sources[v] === 'loading';
  // Rows a venue shows while its own read is not ready are a last reading, not a
  // current one. Saying so is the difference between a stale reading and a
  // wrong one — but only for the venue whose rows are on the card: a Kalshi row
  // is no reading of a failed Polymarket read (#1068). A remounted card also
  // starts from the account's last Polymarket reading while its own first read
  // is on the wire, however long that read takes.
  //
  // Every venue that is not ready gets its own clause, failures first, so a
  // venue with nothing to show is never read as settled beside another's rows.
  const lastKnown = (v: PositionVenue) => projection.lastKnownVenues.includes(v);
  const groups: [PositionVenue[], string][] = [
    [venues.filter((v) => failed(v) && lastKnown(v)), 'position data unavailable — showing the last reading.'],
    [venues.filter((v) => failed(v) && !lastKnown(v)), 'position data unavailable.'],
    [venues.filter((v) => loading(v) && lastKnown(v)), 'position data loading — showing the last reading.'],
    [venues.filter((v) => loading(v) && !lastKnown(v)), 'position data loading.'],
  ];
  const present = groups.filter(([vs]) => vs.length > 0);
  if (present.length > 0) {
    // Only venues loading with nothing to show yet: the plain wording.
    if (present.length === 1 && present[0] === groups[3]) return 'Position data loading…';
    return present
      .map(([vs, words]) => `${vs.map((v) => VENUE_LABEL[v]).join(' and ')} ${words}`)
      .join(' ');
  }
  if (!projection.authoritative) return 'Position data loading…';
  if (projection.rows.length === 0) return 'No position in this market.';
  return null;
}

export function GamePositionSnapshot({
  game,
  us,
}: {
  game: GameRow;
  /** #1233 — the panel's US market and orders read; omitted where there is no US. */
  us?: GamePositionUsInput;
}) {
  const kalshiTickers = useMemo(
    () =>
      [game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker].filter(
        (t): t is string => !!t,
      ),
    [game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker],
  );

  // Position Tracker's canonical Kalshi source. Reading the same store is
  // important in personal-key mode: the raw venue aggregate can differ from
  // the relay-ledger snapshot after a partial sell. A second position hook here
  // would make two dashboard surfaces disagree about both quantity and average.
  const accountPositions = useAccountStore((state) => state.positions);
  const accountStatus = useAccountStore((state) => state.status);
  const accountError = useAccountStore((state) => state.error);
  // Before the first key-mode read a 'disconnected' account is still being
  // checked, not unavailable.
  const kalshiChecking = useAccountStore(
    (state) => state.status === 'disconnected' && state.credentialsStatus === 'unknown',
  );
  const kalshiPositionByTicker = useMemo(
    () => accountPositionsToSlices(accountPositions, new Set(kalshiTickers)),
    [accountPositions, kalshiTickers],
  );
  // Positions only — `includePnl: false` skips the /pnl round-trip entirely.
  // This card shows no P&L, so fetching it would be pure traffic.
  // #1068 — the order panel remounts this card whenever the selected market's
  // identity changes, so it starts from the account's last confirmed reading
  // rather than dropping a held Polymarket position to "loading".
  const poly = usePolymarketRelayPositions({ includePnl: false, seedFromLastReading: true });

  const projection = useMemo(
    () =>
      projectGamePosition({
        game,
        kalshi: {
          positionByTicker: kalshiPositionByTicker,
          loading: accountStatus === 'connecting' || kalshiChecking,
          error:
            !kalshiChecking && (accountStatus === 'error' || accountStatus === 'disconnected')
              ? accountError ?? 'Kalshi position data unavailable'
              : null,
          snapshotSeq: accountStatus === 'connected' ? 1 : 0,
        },
        polymarket: { positions: poly.positions, status: poly.status },
        polyOutcomeLabel: polyPositionBetLabel,
        kalshiOutcomeLabel,
        polymarketUs: us,
      }),
    [
      game,
      kalshiPositionByTicker,
      accountStatus,
      accountError,
      kalshiChecking,
      poly.positions,
      poly.status,
      us,
    ],
  );

  // No venue lists a market on this game, so there is no position to report
  // either way. Rendering the header over "No position in this market." would
  // be stating a fact about a market that does not exist here.
  const applicable = (Object.keys(projection.sources) as PositionVenue[]).filter(
    (v) => projection.sources[v] !== 'absent',
  );
  if (applicable.length === 0) return null;

  const notice = snapshotNotice(projection);

  return (
    <div className="border-border border-b px-4 py-2" data-testid="game-position-snapshot">
      <div
        className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase"
        title="Contracts you hold on the exact selected market, with the canonical before-fees average entry — the same basis the Positions page shows. Sides are venue-native."
      >
        Your position — this market
      </div>
      {projection.rows.map((row) => (
        <div
          key={row.key}
          data-testid="game-position-row"
          data-venue={row.venue}
          data-side={row.side}
          className="flex flex-wrap items-baseline gap-x-2 font-mono text-xs"
        >
          <span className="text-muted-foreground">
            {VENUE_LABEL[row.venue]} · {row.outcomeLabel ?? 'outcome unavailable'} ·{' '}
            {row.side.toUpperCase()}
          </span>
          <span title={row.exactQuantity?.title ?? undefined}>{heldClause(row)}</span>
        </div>
      ))}
      {notice && <div className="text-muted-foreground text-xs">{notice}</div>}
    </div>
  );
}
